import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';
import { useStackModalContext } from '../context/StackModalContext';

const TemplateConfirmationDialog: React.FC = () => {
  const {
    state: {
      isTemplateConfirmOpen,
      templatesForConfirmation,
      controlModeIndexForConfirmation,
      controlModes,
    },
    actions: {
      template: { handleCloseTemplateConfirm, handleConfirmTemplates },
    },
  } = useStackModalContext();

  const controlModeName =
    controlModeIndexForConfirmation !== null &&
    controlModes &&
    controlModes[controlModeIndexForConfirmation]
      ? controlModes[controlModeIndexForConfirmation].name
      : 'this control mode';

  const templateNames =
    templatesForConfirmation && templatesForConfirmation.length > 0
      ? templatesForConfirmation.map((template) => template.name).join(', ')
      : '';

  const isMultipleTemplates =
    templatesForConfirmation && templatesForConfirmation.length > 1;

  const isControlModeEmpty =
    controlModeIndexForConfirmation === null ||
    !controlModes ||
    !controlModes[controlModeIndexForConfirmation] ||
    !controlModes[controlModeIndexForConfirmation].sections ||
    controlModes[controlModeIndexForConfirmation].sections.length === 0;

  return (
    <Dialog
      open={isTemplateConfirmOpen ?? false}
      onClose={handleCloseTemplateConfirm}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        Confirm Apply {isMultipleTemplates ? 'Templates' : 'Template'}
      </DialogTitle>
      <DialogContent>
        <List disablePadding>
          <ListItem alignItems='flex-start' sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <InfoIcon color='primary' />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant='body1'>
                  Are you sure you want to apply the selected{' '}
                  {isMultipleTemplates ? 'templates' : 'template'}?
                </Typography>
              }
              secondary={
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  sx={{ mt: 1, mb: 0 }}
                >
                  {templateNames}
                </Typography>
              }
            />
          </ListItem>

          <ListItem alignItems='flex-start' sx={{ px: 0 }}>
            <ListItemText
              sx={{ marginLeft: '40px' }}
              primary={
                <Typography variant='body1'>
                  To the following control mode:
                </Typography>
              }
              secondary={
                <Typography variant='body1' fontWeight='bold' sx={{ mt: 1 }}>
                  {controlModeName}
                </Typography>
              }
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2, opacity: 0.8 }} />

        {!isControlModeEmpty && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(245, 124, 0, 0.2)',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <WarningAmberIcon color='warning' sx={{ mr: 1, mt: 0.5 }} />
              <Box>
                <Typography
                  variant='body1'
                  color='warning.dark'
                  fontWeight='bold'
                >
                  Warning:
                </Typography>
                <Typography variant='body1' color='warning.main'>
                  All previously selected sections and their fields will be
                  overwritten by the selected{' '}
                  {isMultipleTemplates ? 'templates' : 'template'}.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCloseTemplateConfirm}
          color='primary'
          variant='outlined'
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmTemplates}
          color='primary'
          variant='contained'
          autoFocus
        >
          Apply {isMultipleTemplates ? 'Templates' : 'Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(TemplateConfirmationDialog);
